import { useLocation, useNavigate } from 'react-router-dom';

import { api } from '@/lib/api-client';
import { storeGameSandbox } from '@/lib/utils';

/**
 * Updates the API defaults with new game and sandbox codes.
 * Must be called BEFORE invalidating queries so refetches use the new context.
 */
export const updateApiDefaults = (gameCode: string, sandboxCode: string) => {
  api.defaults.params = {
    ...(api.defaults.params as Record<string, string> | undefined),
    game_code: gameCode,
    sandbox_code: sandboxCode,
  };
};

/**
 * Get the safe navigation path when switching sandboxes.
 * If the user is on an entity-specific page (e.g., player detail, snapshot detail),
 * navigate to the parent list to avoid 404 errors.
 * Returns an object with path and whether to preserve query params.
 */
export const getSafeNavigationPath = (
  currentPath: string,
  gameCode: string,
  newSandbox: string,
): { path: string; preserveSearch: boolean } => {
  const basePath = `/game/${gameCode}/${newSandbox}`;

  // Pattern: /game/:gameCode/:sandboxCode/players/:playerId/...
  // Navigate to /game/:gameCode/:sandboxCode/players
  // Don't preserve search params when navigating away from detail page
  const playerDetailPattern = /\/players\/\d+/;
  if (playerDetailPattern.test(currentPath)) {
    return { path: `${basePath}/players`, preserveSearch: false };
  }

  // Pattern: /game/:gameCode/:sandboxCode/store/snapshot/:snapshotId
  // Navigate to /game/:gameCode/:sandboxCode/store/snapshot/new
  // Don't preserve search params when navigating away from detail page
  const snapshotDetailPattern = /\/store\/snapshot\/(?!new)[^/]+/;
  if (snapshotDetailPattern.test(currentPath)) {
    return { path: `${basePath}/store/snapshot/new`, preserveSearch: false };
  }

  // For all other routes, just replace the sandbox in the current path
  // Preserve search params since we're staying on the same type of page
  const pathAfterSandbox = currentPath.replace(
    /^\/game\/[^/]+\/[^/]+/,
    basePath,
  );
  return { path: pathAfterSandbox, preserveSearch: true };
};

interface UseGameSwitchOptions {
  onBeforeSwitch?: () => void;
}

/**
 * Hook for switching between games.
 * Navigates to the sandboxes page for the new game.
 */
export const useGameSwitch = (options: UseGameSwitchOptions = {}) => {
  const navigate = useNavigate();

  const switchGame = (currentGameCode: string, newGameCode: string) => {
    if (newGameCode === currentGameCode) return;

    options.onBeforeSwitch?.();

    // Navigate to sandboxes page for the new game
    navigate(`/game/${newGameCode}/sandboxes`);
  };

  return { switchGame };
};

interface UseSandboxSwitchOptions {
  onBeforeSwitch?: () => void;
}

/**
 * Hook for switching between sandboxes within a game.
 * Handles API defaults, localStorage persistence, and safe navigation.
 * Preserves URL query parameters when staying on the same type of page.
 */
export const useSandboxSwitch = (options: UseSandboxSwitchOptions = {}) => {
  const navigate = useNavigate();
  const location = useLocation();

  const switchSandbox = (
    gameCode: string,
    currentSandbox: string,
    newSandbox: string,
  ) => {
    if (newSandbox === currentSandbox) return;

    options.onBeforeSwitch?.();

    // Update API defaults for the new sandbox
    updateApiDefaults(gameCode, newSandbox);

    // Persist to localStorage
    storeGameSandbox(gameCode, newSandbox);

    // Navigate to safe path (avoid 404 on entity-specific pages)
    // Preserve query params when staying on the same type of page (e.g., list filters)
    const { path, preserveSearch } = getSafeNavigationPath(
      location.pathname,
      gameCode,
      newSandbox,
    );
    const fullPath = preserveSearch ? `${path}${location.search}` : path;
    navigate(fullPath);
  };

  return { switchSandbox };
};
